// Goal, Todo의 description(content) 편집에 사용

import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState, useRef, useCallback } from 'react';

export function useContentEdit<T extends { id: string; content: string }>(
    item: T,
    updateFunction?: (id: string, fields: Partial<T>) => Promise<T>,
    debounceTime = 1000
) {
    const [content, setContent] = useState(item?.content || '');
    const [debouncedContent] = useDebouncedValue(content, debounceTime);
    // 사용자가 실제로 내용을 수정했는지 추적하는 플래그
    const hasUserEdited = useRef(false);
    // 현재 아이템 참조 보관
    const itemRef = useRef(item);
    // updateFunction 참조 보관
    const updateFunctionRef = useRef(updateFunction);

    // 참조 업데이트
    useEffect(() => {
        itemRef.current = item;
        updateFunctionRef.current = updateFunction;
    }, [item, updateFunction]);

    // 아이템이 변경되면 내용 초기화
    useEffect(() => {
        setContent(item?.content || '');
        // 새 아이템으로 변경 시 편집 플래그 초기화
        hasUserEdited.current = false;
    }, [item?.id, item?.content]);

    // 내용 업데이트 함수 메모이제이션
    const updateItemContent = useCallback(async () => {
        // updateFunction이 없으면 업데이트 스킵
        if (!updateFunctionRef.current) return;

        const currentItem = itemRef.current;
        // 실제 내용이 변경되었고, 사용자가 편집했을 때만 업데이트
        if (
            debouncedContent !== currentItem?.content &&
            hasUserEdited.current
        ) {
            try {
                await updateFunctionRef.current(currentItem.id, {
                    content: debouncedContent,
                } as Partial<T>);
            } catch (error) {
                console.error(
                    `Failed to update ${currentItem.id} content: `,
                    error
                );
            }
        }
    }, [debouncedContent]);

    // 디바운스된 내용이 변경될 때만 업데이트 실행
    useEffect(() => {
        void updateItemContent();
    }, [updateItemContent]);

    // 원래 setContent 함수를 감싸서 사용자 편집 여부 표시
    const handleSetContent = (newContent: string) => {
        hasUserEdited.current = true;
        setContent(newContent);
    };

    return [content, handleSetContent] as const;
}
